import { Outlet, useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { onboardingApi } from './services/onboardingApi';
import { OnboardingProject } from '@/types/onboarding';
import { ONBOARDING_STAGES } from '@/types/onboarding';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowLeft, CheckCircle2, Circle } from 'lucide-react';
import StageProgressBar from './components/StageProgressBar';
import BreadcrumbNavigation from './components/BreadcrumbNavigation';
import StageBreadcrumbNavigation from './components/StageBreadcrumbNavigation';

const OnboardingLayout = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { currentWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const [project, setProject] = useState<OnboardingProject | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (projectId && projectId !== 'new') {
      loadProject();
    } else {
      setLoading(false);
    }
  }, [projectId]);

  const loadProject = async () => {
    if (!projectId || projectId === 'new') {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await onboardingApi.getProject(projectId);
      setProject(data);
    } catch (error: any) {
      console.error('Failed to load project:', error);
      // Set project to null on error to show error state
      setProject(null);
    } finally {
      setLoading(false);
    }
  };

  const handleStageClick = (stagePath: string) => {
    if (projectId === 'new') return; // Don't allow navigation in new project
    navigate(`/w/${currentWorkspace?.id}/dashboard/tum/onboarding/project/${projectId}/${stagePath}`);
  };

  const handleExit = () => {
    navigate(`/w/${currentWorkspace?.id}/dashboard/tum/onboarding`);
  };

  const formatStatus = (status: string) => {
    const statusMap: Record<string, string> = {
      draft: 'Draft',
      in_progress: 'In Progress',
      pending_approval: 'Pending Approval',
      approved: 'Approved',
      live: 'Live',
      cancelled: 'Cancelled',
    };
    return statusMap[status] || status;
  };

  if (loading && projectId !== 'new') {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading project...</div>
        </div>
      </div>
    );
  }

  // Map database stage names to stage paths
  const stageNameToPath: Record<string, string> = {
    'initiated': 'stage-1',
    'data_import': 'stage-2',
    'validation': 'stage-3',
    'mapping': 'stage-4',
    'configuration': 'stage-5',
    'review': 'stage-6',
    'go_live': 'stage-7',
    'completed': 'stage-7',
  };

  const currentStageIndex = project
    ? ONBOARDING_STAGES.findIndex(s => {
        const expectedPath = stageNameToPath[project.current_stage] || project.current_stage.replace('_', '-');
        return s.path === expectedPath || s.path === `stage-${expectedPath.replace('stage-', '')}`;
      })
    : 0;

  // Build breadcrumb items with stage information
  const getStageLabel = (stage: string) => {
    const stageMap: Record<string, string> = {
      'initiated': 'Stage 1: Initiation',
      'data_import': 'Stage 2: Data Import',
      'validation': 'Stage 3: Validation',
      'mapping': 'Stage 4: Mapping',
      'configuration': 'Stage 5: Configuration',
      'review': 'Stage 6: Review',
      'go_live': 'Stage 7: Go-Live',
      'completed': 'Completed',
    };
    return stageMap[stage] || stage;
  };

  const breadcrumbItems = [
    { label: 'TUM', path: `/w/${currentWorkspace?.id}/dashboard/tum` },
    { label: 'Onboarding', path: `/w/${currentWorkspace?.id}/dashboard/tum/onboarding` },
    ...(project ? [
      { label: project.customer_name || 'Project', path: `/w/${currentWorkspace?.id}/dashboard/tum/onboarding/project/${project.id}/stage-1` },
      { label: getStageLabel(project.current_stage), path: undefined, isActive: true }
    ] : []),
  ];

  return (
    <div className="onboarding-wizard min-h-screen bg-background w-full">
      {/* Breadcrumb Navigation */}
      <div className="border-b bg-muted/30">
        <div className="w-full px-6 py-3">
          <BreadcrumbNavigation items={breadcrumbItems} />
        </div>
      </div>

      {/* Header */}
      <div className="border-b bg-card">
        <div className="w-full px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={handleExit} className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Projects
              </Button>
              {project && (
                <div>
                  <h2 className="text-xl font-semibold">{project.customer_name}</h2>
                  <p className="text-sm text-muted-foreground">{project.customer_name}</p>
                </div>
              )}
            </div>
            {project && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Status:</span>
                <span className={`text-sm font-medium ${
                  project.overall_status === 'live' ? 'text-green-600' :
                  project.overall_status === 'in_progress' ? 'text-blue-600' :
                  project.overall_status === 'pending_approval' ? 'text-amber-600' :
                  'text-muted-foreground'
                }`}>
                  {formatStatus(project.overall_status)}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stage Breadcrumb Navigation */}
      <div className="border-b bg-muted/30">
        <div className="w-full px-6 py-4">
          {project && projectId && projectId !== 'new' ? (
            <StageBreadcrumbNavigation
              currentStage={project.current_stage}
              projectId={projectId}
              workspaceId={currentWorkspace?.id}
            />
          ) : (
            <StageProgressBar
              stages={ONBOARDING_STAGES}
              currentStage={project?.current_stage || 'initiated'}
              onStageClick={handleStageClick}
              disabled={projectId === 'new'}
            />
          )}
        </div>
      </div>

      {/* Stage Content */}
      <div className="w-full max-w-full px-6 py-8">
        <Outlet context={{ project, setProject, loadProject }} />
      </div>
    </div>
  );
};

export default OnboardingLayout;

